import axios from 'axios';

const OrganizationService = {
  /**
   * Get organization details
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Organization data
   */
  async getOrganization(organizationId) {
    const response = await axios.get(`/organizations/${organizationId}`);
    return response.data;
  },

  /**
   * Update organization details
   * @param {string} organizationId - Organization ID
   * @param {Object} data - Organization update data
   * @param {string} data.name - Organization name
   * @param {string} data.billingAddress - Billing address
   * @param {string} data.city - City
   * @param {string} data.state - State
   * @param {string} data.zip - ZIP code
   * @returns {Promise<Object>} Updated organization data
   */
  async updateOrganization(organizationId, data) {
    const response = await axios.patch(`/organizations/${organizationId}`, data);
    return response.data;
  },

  async uploadOrganizationPhoto(organizationId, image) {
    const form = new FormData();
    form.append('file', image);
    const response = await axios.post(
      `/organizations/${organizationId}/photo`,
      form
    );
    return response.data;
  },

  async deleteOrganizationPhoto(organizationId) {
    const response = await axios.delete(`/organizations/${organizationId}/photo`);
    return response.data;
  },

  /**
   * Get all users in an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of users in the organization
   */
  async getOrganizationUsers(organizationId, options = {}) {
    const includeInactive = options.includeInactive ? { includeInactive: true } : {};
    // Get all users in an organization
    // Backend endpoint: GET /api/organizations/:id/users
    const response = await axios.get(`/organizations/${organizationId}/users`, {
      params: includeInactive,
    });
    // Response should be an array of users with orgRole included
    return Array.isArray(response.data) ? response.data : response.data.rows || [];
  },

  /**
   * Add user to organization
   * @param {Object} data - User data
   * @param {string} data.organizationId - Organization ID
   * @param {string|Array<string>} data.emails - Email(s) of user(s) to add
   * @param {string} data.orgRoleId - Organization role ID
   * @param {string} data.message - Optional message
   * @returns {Promise<Object>} Response data
   */
  async addOrganizationUser(data) {
    // Add user(s) to organization
    // Backend endpoint: POST /api/organizations/:id/users
    const response = await axios.post(`/organizations/${data.organizationId}/users`, {
      emails: data.emails,
      orgRoleId: data.orgRoleId,
      message: data.message,
      newUsers: data.newUsers,
      folderIds: data.folderIds,
    });
    return response.data;
  },

  /**
   * Update user's details (name, email, title, organization role)
   * @param {Object} data - Update data
   * @param {string} data.organizationId - Organization ID
   * @param {string} data.userId - User ID
   * @param {string} data.name - User's full name (optional)
   * @param {string} data.email - User's email (optional)
   * @param {string} data.title - User's title (optional)
   * @param {string} data.orgRoleId - New organization role ID (optional)
   * @returns {Promise<Object>} Updated user data
   */
  async updateOrganizationUser(data) {
    const updateData = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.email !== undefined) updateData.email = data.email;
    if (data.title !== undefined) updateData.title = data.title;
    if (data.orgRoleId !== undefined) updateData.orgRoleId = data.orgRoleId;
    if (data.isActive !== undefined) updateData.isActive = data.isActive;

    const response = await axios.patch(
      `/organizations/${data.organizationId}/users/${data.userId}`,
      updateData
    );
    return response.data;
  },

  /**
   * Remove user from organization
   * @param {Object} data - Remove data
   * @param {string} data.organizationId - Organization ID
   * @param {string} data.userId - User ID
   * @returns {Promise<Object>} Response data
   */
  async removeOrganizationUser(data) {
    const response = await axios.delete(
      `/organizations/${data.organizationId}/users/${data.userId}`
    );
    return response.data;
  },

  /**
   * Resend organization invitation email to an existing user
   * @param {Object} data - Resend data
   * @param {string} data.organizationId - Organization ID
   * @param {string} data.userId - User ID
   * @returns {Promise<Object>} Response data
   */
  async resendOrganizationInvite(data) {
    const response = await axios.post(
      `/organizations/${data.organizationId}/users/${data.userId}/resend-invite`
    );
    return response.data;
  },

  /**
   * Get all available organization roles
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} Array of organization roles
   */
  async getOrganizationRoles(organizationId) {
    const response = await axios.get(`/organizations/${organizationId}/roles`);
    return response.data;
  },

  /**
   * Get all root folders in an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} Array of root folders
   */
  async getOrganizationFolders(organizationId) {
    const response = await axios.get(`/organizations/${organizationId}/folders`);
    return response.data;
  },
};

export default OrganizationService;
